import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@mxa-argos/components/shadcn/tooltip';
import { cn } from '@mxa-argos/lib/utils';
import { useSystemStatus } from '@mxa-argos/modules/system-status/hooks/use-system-status';
import {
  STATUS_PAGE_URL,
  type SystemStatus,
} from '@mxa-argos/modules/system-status/services/system-status.service';
import { useTranslation } from 'react-i18next';

interface StatusVisual {
  core: string;
  pill: string;
  live: boolean;
}

const VISUALS: Record<SystemStatus | 'unknown', StatusVisual> = {
  up: {
    core: 'bg-mxa-green',
    pill: 'border-border/60 text-muted-foreground hover:bg-accent',
    live: true,
  },
  degraded: {
    core: 'bg-mxa-orange',
    pill: 'border-mxa-orange/30 bg-mxa-orange/10 text-mxa-orange hover:bg-mxa-orange/15',
    live: true,
  },
  maintenance: {
    core: 'bg-mxa-orange',
    pill: 'border-border/60 text-muted-foreground hover:bg-accent',
    live: true,
  },
  down: {
    core: 'bg-mxa-red',
    pill: 'border-mxa-red/30 bg-mxa-red/10 text-mxa-red hover:bg-mxa-red/15',
    live: true,
  },
  unknown: {
    core: 'bg-mxa-grey',
    pill: 'border-border/60 text-muted-foreground hover:bg-accent',
    live: false,
  },
};

export const SystemStatusIndicator: React.FC = () => {
  const { t } = useTranslation();
  const { status, isError } = useSystemStatus();

  const key: SystemStatus | 'unknown' = isError || status === null ? 'unknown' : status;
  const visual = VISUALS[key];
  const shortLabel = t(`systemStatus.short.${key}`);
  const fullLabel = t(`systemStatus.${key}`);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <a
            href={STATUS_PAGE_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={fullLabel}
            className={cn(
              'group inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-xs font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring',
              visual.pill,
            )}
          >
            <span className="relative flex size-2">
              {visual.live && (
                <span
                  aria-hidden
                  className={cn(
                    'absolute inline-flex h-full w-full animate-ping rounded-full opacity-60 motion-reduce:hidden',
                    visual.core,
                  )}
                />
              )}
              <span className={cn('relative inline-flex size-2 rounded-full', visual.core)} />
            </span>
            <span className="whitespace-nowrap">{shortLabel}</span>
          </a>
        </TooltipTrigger>
        <TooltipContent>{fullLabel}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
